import {
  Geo,
  InputStyle,
  InputStyleWrapper,
  InputValidationMessage,
  LocationSelect,
} from '@getvim/atomic-ui';
import React, { useCallback, useMemo } from 'react';
import { orderAssistLogger } from '../../logger';
import { Address, InputWrapperProps } from '../../types';
import { formatGeoToAddress, formatToGeo } from '../../utils/addressFormatter';
import './AddressInputWrapper.less';

interface AddressInputWrapperProps extends InputWrapperProps<'address'> {
  isGoogleApiLoaded: boolean;
  value: Address | undefined;
  validationError?: string;
  isDirty?: boolean;
}

const AddressInputWrapper: React.FC<AddressInputWrapperProps> = ({
  onChange,
  isGoogleApiLoaded,
  value,
  validationError,
  isDirty,
}: AddressInputWrapperProps) => {
  const setInputChange = (geoInput: Geo | null) => {
    onChange({ address: formatGeoToAddress(geoInput) });
  };

  const shouldFetchGeo = useCallback(() => {
    const shouldFetch = value && !value?.zipCode && value.address1 && value.city;
    if (shouldFetch) {
      orderAssistLogger.info('Fetching GEO from GoogleAPI', { ehrAddress: value });
    }
    return shouldFetch;
  }, [value]);

  const geo: Geo | undefined = useMemo(() => formatToGeo(value), [value]);

  // missing zip -> let LocationSelect fetch it; coords AND zip -> pass geo so formatToGeo attaches
  // the geocode and the field shows one stable "City, ST zip" (no blur resync); anything else (incl.
  // coords without zip, which would otherwise re-geocode and resync) -> display only.
  const locationProps = useMemo(
    () =>
      shouldFetchGeo() || (value?.coordinates && value?.zipCode)
        ? { geo }
        : { defaultAddressInput: geo?.address },
    [geo, shouldFetchGeo, value],
  );

  return (
    <InputStyleWrapper
      className="location-input-wrapper input-wrapper "
      leftIcon={<i className="icon-search-2" />}
      rightIcon={<></>}
      inputStyle={InputStyle.pillMedium}
      hasError={isDirty && !!validationError}
    >
      <div className="input">
        <LocationSelect
          label="Location"
          placeholder=" "
          showChevronIcon
          isGoogleApiLoaded={isGoogleApiLoaded}
          onChange={(changedGeo) => setInputChange(changedGeo)}
          {...locationProps}
        />
      </div>
      {isDirty && !!validationError ? (
        <InputValidationMessage>{validationError}</InputValidationMessage>
      ) : null}
    </InputStyleWrapper>
  );
};

export default AddressInputWrapper;
